import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./Organigramme.css";
import { getOrganigrammeRequest } from "../../../redux/actions/entitesActions";
import { getAllUsersRequest } from "../../../redux/actions/userActions";
import { Search, X, Users, ChevronDown, ChevronRight, ArrowLeft } from "lucide-react";
import { API_BASE_URL } from "../../../../api";

function flatEntities(tree = []) {
    const out = [];
    const walk = (node) => { out.push(node); (node.children || []).forEach(walk); };
    tree.forEach(walk);
    return out;
}

function buildDirectReportsIndex(allUsers = []) {
    const idx = new Map();
    allUsers.forEach((u) => {
        const mid = u.managerId || u.manager?.id;
        if (!mid) return;
        idx.set(mid, (idx.get(mid) || 0) + 1);
    });
    return idx;
}

function fullName(u) {
    if (!u) return "";
    return `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.fullName || u.email || "—";
}

function normalizeUser(u) {
    if (!u) return null;
    return {
        id: u.id,
        fullName: fullName(u),
        email: u.email,
        phone: u.phone,
        city: u.city,
        profilePictureUrl: u.profilePictureUrl,
        jobName: u.job?.name || u.jobName || null,
        jobCode: u.job?.code || u.jobCode || null,
        managerId: u.manager?.id || u.managerId || null,
        entityName: u.entityName || u.entity?.name || null,
        entityCode: u.entityCode || u.entity?.code || null,
    };
}

function Avatar({ url, name, size = 36 }) {
    const initials = (name || "U")[0]?.toUpperCase();
    const imgUrl = url ? (url.startsWith("http") ? url : `${API_BASE_URL}/api/users/images/profile/${url}`) : null;
    return (
        <span className="av" style={{ "--av-size": `${size}px` }}>
            {imgUrl
                ? <img src={imgUrl} alt={name}
                       onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }} />
                : null
            }
            <span className="av__initials" style={{ display: imgUrl ? "none" : "flex" }}>{initials}</span>
        </span>
    );
}

export default function Organigramme() {
    const dispatch = useDispatch();
    const { organigramme, organigrammeError, entitesLoading } = useSelector((s) => s.entite);
    const { users, loading: userLoading } = useSelector((s) => s.user);

    const [rootEntityId, setRootEntityId] = useState("ALL");
    const [horizontal, setHorizontal] = useState(false);
    const [search, setSearch] = useState("");
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [expanded, setExpanded] = useState(() => new Set());

    const toggleExpand = (id) => setExpanded((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    useEffect(() => {
        dispatch(getOrganigrammeRequest());
        dispatch(getAllUsersRequest());
    }, [dispatch]);

    useEffect(() => {
        if (organigramme?.length > 0) {
            setExpanded(new Set(organigramme.map(r => r.id)));
        }
    }, [organigramme]);

    const orgTree = organigramme || [];
    const allEntities = useMemo(() => flatEntities(orgTree), [orgTree]);

    const rootOptions = useMemo(() => [
        { id: "ALL", name: "Tous les départements" },
        ...allEntities.map((e) => ({ id: String(e.id), name: e.name }))
    ], [allEntities]);

    const visibleTree = useMemo(() => {
        if (rootEntityId === "ALL") return orgTree;
        const idNum = Number(rootEntityId);
        const find = (nodes) => {
            for (const n of nodes) {
                if (n.id === idNum) return [n];
                const found = find(n.children || []);
                if (found) return found;
            }
            return null;
        };
        return find(orgTree) || [];
    }, [orgTree, rootEntityId]);

    const allMembersFlat = useMemo(() => {
        const out = [];
        const walk = (node) => {
            (node.members || []).forEach((m) => out.push({ ...m, __entity: node }));
            (node.children || []).forEach(walk);
        };
        visibleTree.forEach(walk);
        return out;
    }, [visibleTree]);

    const searchMatches = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return [];
        return allMembersFlat.filter((m) => {
            return (m.fullName || "").toLowerCase().includes(q)
                || (m.email || "").toLowerCase().includes(q)
                || (m.city || "").toLowerCase().includes(q);
        }).slice(0, 8);
    }, [search, allMembersFlat]);

    const directReportsIndex = useMemo(() => buildDirectReportsIndex(users || []), [users]);
    const userById = useMemo(() => {
        const map = new Map();
        (users || []).forEach((u) => map.set(u.id, u));
        return map;
    }, [users]);

    const openEntityPanel = (entity) => { setSelectedUser(null); setSelectedEntity(entity); };
    const openUserPanel = (user, entity) => { setSelectedEntity(null); setSelectedUser({ ...user, __entity: entity || user.__entity }); };
    const closePanels = () => { setSelectedEntity(null); setSelectedUser(null); };

    const isLoading = !!entitesLoading || !!userLoading;

    return (
        <div className="org">
            {/* TOOLBAR */}
            <div className="org__toolbar">
                <div className="org__toolbarLeft">
                    {/*<button className="org__backBtn" type="button" onClick={() => window.history.back()}>*/}
                    {/*    <ArrowLeft size={16} />*/}
                    {/*</button>*/}
                    <div>
                        <h1 className="org__title">Organigramme</h1>
                        <p className="org__subtitle">{allMembersFlat.length} collaborateurs · {allEntities.length} départements</p>
                    </div>
                </div>
                <div className="org__toolbarRight">
                    {/* Search */}
                    <div className="org__searchWrap">
                        <Search className="org__searchIcon" size={14} />
                        <input
                            className="org__search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Rechercher un collaborateur…"
                        />
                        {search && (
                            <button className="org__searchClear" onClick={() => setSearch("")} type="button">
                                <X size={12} />
                            </button>
                        )}
                        {!!searchMatches.length && (
                            <div className="org__suggest">
                                {searchMatches.map((m) => (
                                    <button key={`${m.id}-${m.__entity?.id}`} type="button"
                                            className="org__suggestItem"
                                            onClick={() => { openUserPanel(m, m.__entity); setSearch(""); }}>
                                        <Avatar url={m.profilePictureUrl} name={m.fullName} size={30} />
                                        <div>
                                            <div className="org__suggestName">{m.fullName}</div>
                                            <div className="org__suggestMeta">{m.__entity?.name} · {m.jobName || "—"}</div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Select */}
                    <select className="org__select" value={rootEntityId} onChange={(e) => setRootEntityId(e.target.value)}>
                        {rootOptions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>

                    {/* Toggle */}
                    <label className="org__toggleWrap">
                        <span className="org__toggleLabel">Horizontal</span>
                        <div className="org__switch">
                            <input type="checkbox" checked={horizontal} onChange={(e) => setHorizontal(e.target.checked)} />
                            <span className="org__slider" />
                        </div>
                    </label>
                </div>
            </div>

            {/* CANVAS */}
            <div className={`org__canvas ${horizontal ? "is-horizontal" : ""}`}>
                {isLoading && <div className="org__hint">Chargement…</div>}
                {organigrammeError && <div className="org__error">{organigrammeError}</div>}
                <OrgTree
                    nodes={visibleTree}
                    expanded={expanded}
                    onToggleExpand={toggleExpand}
                    onOpenEntity={openEntityPanel}
                    onOpenUser={openUserPanel}
                />
            </div>

            {/* MODAL */}
            {(selectedEntity || selectedUser) && (
                <div className="orgModal" role="dialog" aria-modal="true">
                    <div className="orgModal__overlay" onClick={closePanels} />
                    <div className="orgModal__panel">
                        {selectedEntity && (
                            <EntityPanel entity={selectedEntity} onOpenUser={openUserPanel}
                                         directReportsIndex={directReportsIndex} onClose={closePanels} />
                        )}
                        {selectedUser && (
                            <UserPanel user={selectedUser} users={users || []}
                                       directReportsIndex={directReportsIndex} userById={userById}
                                       onOpenUser={openUserPanel} onClose={closePanels} />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function OrgTree({ nodes, expanded, onToggleExpand, onOpenEntity, onOpenUser, level = 0 }) {
    if (!nodes?.length) return <div className="org__hint">Aucune donn\u00e9e.</div>;
    const entityLevelLabel = level === 0 ? "D\u00e9partement" : level === 1 ? "Service" : "Section";
    return (
        <div className="orgtree">
            {nodes.map((n) => {
                const hasChildren = (n.children || []).length > 0;
                const isOpen = expanded.has(n.id);
                const colorLevel = Math.min(level, 2);
                return (
                    <div key={n.id} className={`orgblock level-${level} orgblock--color-${colorLevel}`}>
                        <div className="orgcard">
                            <button type="button" className="orgcard__main" onClick={() => onOpenEntity(n)}>
                                <div className="orgcard__left">
                                    <div className="orgcard__top">
                                        <div className="orgcard__name">{n.name}</div>
                                        <span className="orgcard__level">{entityLevelLabel}</span>
                                    </div>
                                    <div className="orgcard__meta">
                                        <span className="orgcard__pill">
                                            <Users size={11} /> {(n.members || []).length} membre{(n.members || []).length !== 1 ? "s" : ""}
                                        </span>
                                        {n.responsable && (
                                            <button type="button" className="orgcard__resp"
                                                    onClick={(e) => { e.stopPropagation(); onOpenUser(n.responsable, n); }}>
                                                <Avatar url={n.responsable.profilePictureUrl} name={n.responsable.fullName} size={22} />
                                                <span>{n.responsable.fullName}</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </button>
                            {hasChildren && (
                                <button className="orgcard__toggle" type="button" onClick={() => onToggleExpand(n.id)}>
                                    {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                                </button>
                            )}
                        </div>
                        {hasChildren && isOpen && (
                            <div className="orgchildren">
                                <OrgTree nodes={n.children} expanded={expanded}
                                         onToggleExpand={onToggleExpand} onOpenEntity={onOpenEntity}
                                         onOpenUser={onOpenUser} level={level + 1} />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function ModalHeader({ onClose, children }) {
    return (
        <div className="orgModal__header">
            {children}
            <button className="orgModal__closeBtn" onClick={onClose} type="button" aria-label="Fermer">
                <X size={15} />
            </button>
        </div>
    );
}

function EntityPanel({ entity, onOpenUser, directReportsIndex, onClose }) {
    const members = entity.members || [];
    return (
        <>
            <ModalHeader onClose={onClose}>
                <div className="orgModal__headerInfo">
                    <div className="orgModal__headerName">{entity.name}</div>
                    <div className="orgModal__headerSub">{members.length} membre{members.length !== 1 ? "s" : ""}</div>
                </div>
            </ModalHeader>
            <div className="orgModal__body">
                {entity.responsable && (
                    <div className="orgModal__section">
                        <div className="orgModal__sectionTitle">Responsable</div>
                        <button type="button" className="orgModal__personBtn"
                                onClick={() => onOpenUser(entity.responsable, entity)}>
                            <Avatar url={entity.responsable.profilePictureUrl} name={entity.responsable.fullName} size={40} />
                            <div>
                                <div className="orgModal__personName">{entity.responsable.fullName}</div>
                                <div className="orgModal__personRole">{entity.responsable.jobName || "—"}</div>
                            </div>
                            <ChevronRight size={14} className="orgModal__personArrow" />
                        </button>
                    </div>
                )}
                <div className="orgModal__section">
                    <div className="orgModal__sectionTitle">Membres · {members.length}</div>
                    <div className="orgModal__list">
                        {members.map((m) => {
                            const teamCount = directReportsIndex.get(m.id) || 0;
                            return (
                                <button key={m.id} type="button" className="orgModal__personBtn"
                                        onClick={() => onOpenUser(m, entity)}>
                                    <Avatar url={m.profilePictureUrl} name={m.fullName} size={38} />
                                    <div>
                                        <div className="orgModal__personName">{m.fullName}</div>
                                        <div className="orgModal__personRole">{m.jobName || m.jobCode || "—"}</div>
                                    </div>
                                    {teamCount > 0 && (
                                        <span className="orgModal__badge"><Users size={11} /> {teamCount}</span>
                                    )}
                                    <ChevronRight size={14} className="orgModal__personArrow" />
                                </button>
                            );
                        })}
                        {!members.length && <div className="orgModal__muted">Aucun membre.</div>}
                    </div>
                </div>
            </div>
        </>
    );
}

function UserPanel({ user, users, userById, onOpenUser, onClose }) {
    const current = useMemo(() => normalizeUser(user) || user, [user]);
    const managerId = current.managerId || current.manager?.id;
    const managerFull = managerId ? userById.get(managerId) : null;
    const manager = managerFull ? normalizeUser(managerFull) : null;

    const teamMembers = useMemo(() => {
        return (users || [])
            .filter((u) => String(u.managerId || u.manager?.id) === String(current.id))
            .map(normalizeUser).filter(Boolean)
            .sort((a, b) => (a.fullName || "").localeCompare(b.fullName || "", "fr"));
    }, [users, current.id]);

    const imgUrl = current.profilePictureUrl
        ? (current.profilePictureUrl.startsWith("http")
            ? current.profilePictureUrl
            : `${API_BASE_URL}/api/users/images/profile/${current.profilePictureUrl}`)
        : null;

    const initials = `${(current.fullName || "U")[0]?.toUpperCase()}`;

    return (
        <>
            <div className="orgModal__userHeader">
                <button className="orgModal__closeBtn orgModal__closeBtn--light" onClick={onClose} type="button">
                    <X size={15} />
                </button>
                <div className="orgModal__userAvatar">
                    {imgUrl
                        ? <img src={imgUrl} alt=""
                               onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }} />
                        : null
                    }
                    <span style={{ display: imgUrl ? "none" : "flex" }}>{initials}</span>
                </div>
                <div className="orgModal__userName">{current.fullName}</div>
                <div className="orgModal__userJob">{current.jobName || current.jobCode || "—"}</div>
                <div className="orgModal__userDept">{user.__entity?.name || current.entityName || "—"}{current.city ? ` · ${current.city}` : ""}</div>
            </div>

            <div className="orgModal__body">
                <div className="orgModal__section">
                    <div className="orgModal__sectionTitle">Coordonnées</div>
                    <div className="orgModal__infoGrid">
                        <div className="orgModal__infoRow"><span className="orgModal__infoLabel">Email</span><span className="orgModal__infoVal">{current.email ? <a href={`mailto:${current.email}`} className="orgModal__link">{current.email}</a> : "—"}</span></div>
                        <div className="orgModal__infoRow"><span className="orgModal__infoLabel">Téléphone</span><span className="orgModal__infoVal">{current.phone ? <a href={`tel:${current.phone}`} className="orgModal__link">{current.phone}</a> : "—"}</span></div>
                        <div className="orgModal__infoRow"><span className="orgModal__infoLabel">Ville</span><span className="orgModal__infoVal">{current.city || "—"}</span></div>
                    </div>
                </div>

                {manager && (
                    <div className="orgModal__section">
                        <div className="orgModal__sectionTitle">Manager</div>
                        <button type="button" className="orgModal__personBtn"
                                onClick={() => onOpenUser({ ...manager, __entity: user.__entity })}>
                            <Avatar url={manager.profilePictureUrl} name={manager.fullName} size={38} />
                            <div>
                                <div className="orgModal__personName">{manager.fullName}</div>
                                <div className="orgModal__personRole">{manager.jobName || "—"}</div>
                            </div>
                            <ChevronRight size={14} className="orgModal__personArrow" />
                        </button>
                    </div>
                )}

                {teamMembers.length > 0 && (
                    <div className="orgModal__section">
                        <div className="orgModal__sectionTitle">Équipe · {teamMembers.length}</div>
                        <div className="orgModal__list">
                            {teamMembers.map((m) => (
                                <button key={m.id} type="button" className="orgModal__personBtn"
                                        onClick={() => onOpenUser({ ...m, __entity: user.__entity })}>
                                    <Avatar url={m.profilePictureUrl} name={m.fullName} size={38} />
                                    <div>
                                        <div className="orgModal__personName">{m.fullName}</div>
                                        <div className="orgModal__personRole">{m.jobName || "—"}</div>
                                    </div>
                                    <ChevronRight size={14} className="orgModal__personArrow" />
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
